import React, { useEffect, useState, memo } from 'react';
import {
  Button,
  Chip,
  FormControlLabel,
  Grid,
  IconButton,
  Switch,
} from '@mui/material';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import RestrictedContentReason from '../form-elements/restricted-content-reason.component';
import RestrictedContent from '../form-elements/restricted-content.component';
import Countries from '../form-elements/countries.component';
import Trashy from '../form-elements/trashy.component';
import GoodBad from '../form-elements/good-bad.component';
import AdTimingStatus from '../form-elements/ad-timing-status.component';
import PublicDomain from '../form-elements/public-domain.component';
import Poster16x9Status from '../form-elements/poster-16x9-status.component';
import Genres from '../form-elements/genres.component';
import TimeInput from '../form-elements/time-input.component';
import Iconify from '../common/iconify.component';
import GenerateAdTimingsModal from '../content/generate-ad-timings.modal.component';
import AlwaysPublished from '../form-elements/always-published.component';
import VideoTypes from '../form-elements/video-types.component';
import Decades from '../form-elements/decades.component';
import { DECADES } from '../../config/const.config';
import Regions from '../form-elements/regions.component';
import MpaaRating from '../form-elements/mpaa-rating.component';
import GenreStatus from '../form-elements/genre-status.component';
import TVODPrice from '../form-elements/tvod-price.component';
import VideoStatus from '../form-elements/video-status.component';
import VideoStatusReason from '../form-elements/video-status-reason.component';
import TrailerStatus from '../form-elements/trailer-status.component';
import TrailerStatusReason from '../form-elements/trailer-status-reason.component';
import QualityCheck from '../form-elements/quality-check.component';
import VideoColor from '../form-elements/video-color.component';
import { PRIMARY, SUCCESS } from '../../theme/theme-colors';
import HTMLDatepicker from '../form-elements/html-datepicker.component';
import LanguageCodes from '../form-elements/language-codes.component';

const styles = {
  input: {
    minWidth: '60px',
    padding: '8px 10px',
    fontSize: '14px',
    border: '1px solid #c4c4c4',
    borderRadius: 4,
    outline: 'none',
  },
  textarea: {
    minWidth: '450px',
    width: '100%',
    padding: '8px 10px',
    fontSize: '14px',
    border: '1px solid #c4c4c4',
    borderRadius: 4,
    resize: 'none',
    outline: 'none',
    height: '160px',
  },
};

const FastTextInput = memo(({ value, onChange, multiline = false, width }) =>
  multiline ? (
    <textarea
      value={value ?? ''}
      onChange={onChange}
      rows={3}
      style={{ ...styles.textarea, width: width || '100%' }}
    />
  ) : (
    <input
      type="text"
      value={value ?? ''}
      onChange={onChange}
      style={styles.input}
    />
  )
);

FastTextInput.propTypes = {
  value: PropTypes.any.isRequired,
  onChange: PropTypes.func.isRequired,
  multiline: PropTypes.bool.isRequired,
  width: PropTypes.string.isRequired,
};

const EditableCellValue = ({ type, dataKey, value, onChange, width }) => {
  if (type === 'textarea') {
    return (
      <FastTextInput
        value={value}
        onChange={onChange}
        multiline
        width={width}
      />
    );
  }
  if (type === 'switch') {
    const checked = typeof value === 'string' ? value === 'YES' : value;
    const isOffline = dataKey === 'offline';

    return (
      <FormControlLabel
        control={
          <Switch
            checked={checked}
            onChange={onChange}
            inputProps={{ 'aria-label': 'controlled' }}
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb': {
                backgroundColor: isOffline ? PRIMARY.main : SUCCESS.main,
              },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                backgroundColor: isOffline ? PRIMARY.light : SUCCESS.light,
              },
              '& .MuiSwitch-thumb': {
                backgroundColor: isOffline ? SUCCESS.main : PRIMARY.main,
              },
              '& .MuiSwitch-track': {
                backgroundColor: isOffline ? SUCCESS.light : PRIMARY.light,
              },
            }}
          />
        }
      />
    );
  }
  if (type === 'date') {
    return (
      <HTMLDatepicker
        id="date"
        name="date"
        label=""
        defaultValue={value}
        clearable
        onChange={onChange}
        // disabled={!hasOperationAccess('manual_assignment', 'manage')}
      />
    );
  }
  if (
    type === 'selection' &&
    (dataKey === 'restricted_content' || dataKey === 'avod_validated')
  ) {
    return (
      <RestrictedContent
        id="restricted_content"
        name="restricted_content"
        label=""
        defaultValue={value}
        onChange={onChange}
        sx={{ width: '100%' }}
        renderFast
      />
    );
  }
  if (type === 'selection' && dataKey === 'restricted_content_reason') {
    return (
      <RestrictedContentReason
        id="restricted_content_reason"
        name="restricted_content_reason"
        defaultValue={value}
        onChange={onChange}
        sx={{ width: '100%' }}
        renderFast
      />
    );
  }
  if (type === 'selection' && dataKey === 'good_bad') {
    return (
      <GoodBad
        id="good_bad"
        name="good_bad"
        defaultValue={value}
        onChange={onChange}
        sx={{ width: '100%' }}
        renderFast
      />
    );
  }
  if (type === 'selection' && dataKey === 'trashy') {
    return (
      <Trashy
        id="trashy"
        name="trashy"
        defaultValue={value}
        onChange={onChange}
        sx={{ width: '100%' }}
        renderFast
      />
    );
  }
  if (type === 'selection' && dataKey === 'country') {
    return (
      <Countries
        id="country"
        name="country"
        label=""
        defaultValue={value}
        onChange={onChange}
        sx={{ width: '100%' }}
        renderFast
      />
    );
  }
  if (type === 'selection' && dataKey === 'ad_timing_status') {
    return (
      <AdTimingStatus
        id="ad_timing_status"
        name="ad_timing_status"
        defaultValue={value}
        onChange={onChange}
        sx={{ width: '100%' }}
        renderFast
      />
    );
  }
  if (type === 'selection' && dataKey === 'public_domain') {
    return (
      <PublicDomain
        id="public_domain"
        name="public_domain"
        defaultValue={value}
        onChange={onChange}
        sx={{ width: '100%' }}
        renderFast
      />
    );
  }
  if (type === 'selection' && dataKey === 'always_published') {
    return (
      <AlwaysPublished
        id="always_published"
        name="always_published"
        defaultValue={value}
        onChange={onChange}
        sx={{ width: '100%' }}
        renderFast
      />
    );
  }
  if (type === 'selection' && dataKey === 'poster_16x9_status') {
    return (
      <Poster16x9Status
        id="poster_16x9_status"
        name="poster_16x9_status"
        defaultValue={value}
        onChange={onChange}
        sx={{ width: '100%' }}
        renderFast
      />
    );
  }
  if (type === 'selection' && dataKey === 'genres') {
    return (
      <Genres
        id="genres"
        name="genres"
        defaultValue={typeof value === 'string' ? JSON.parse(value) : value}
        multiple
        onChange={onChange}
        sx={{ width: '100%' }}
      />
    );
  }
  if (type === 'selection' && dataKey === 'video_type') {
    return (
      <VideoTypes
        id="video_type"
        name="video_type"
        defaultValue={value}
        onChange={onChange}
        sx={{ width: '100%' }}
        renderFast
      />
    );
  }
  if (type === 'selection' && dataKey === 'decade') {
    return (
      <Decades
        id="decade"
        name="decade"
        defaultValue={value}
        onChange={onChange}
        validOptions={Object.keys(DECADES)}
        sx={{ width: '100%' }}
        renderFast
      />
    );
  }
  if (type === 'selection' && dataKey === 'region') {
    return (
      <Regions
        id="region"
        name="region"
        label=""
        defaultValue={value}
        onChange={onChange}
        sx={{ width: '100%' }}
        renderFast
      />
    );
  }
  if (type === 'selection' && dataKey === 'mpaa_rating') {
    return (
      <MpaaRating
        id="mpaa_rating"
        name="mpaa_rating"
        defaultValue={value}
        onChange={onChange}
        sx={{ width: '100%' }}
        renderFast
      />
    );
  }
  if (type === 'selection' && dataKey === 'genre_status') {
    return (
      <GenreStatus
        id="genre_status"
        name="genre_status"
        defaultValue={value}
        onChange={onChange}
        sx={{ width: '100%' }}
        renderFast
      />
    );
  }
  if (type === 'selection' && dataKey === 'tvod_iap') {
    return (
      <TVODPrice
        id="tvod_iap"
        name="tvod_iap"
        defaultValue={value}
        onChange={onChange}
        sx={{ width: '100%' }}
        renderFast
      />
    );
  }
  if (type === 'selection' && dataKey === 'video_status') {
    return (
      <VideoStatus
        id="video_status"
        name="video_status"
        defaultValue={value}
        onChange={onChange}
        sx={{ width: '100%' }}
        renderFast
      />
    );
  }
  if (type === 'selection' && dataKey === 'video_status_reason') {
    return (
      <VideoStatusReason
        id="video_status_reason"
        name="video_status_reason"
        defaultValue={value}
        onChange={onChange}
        sx={{ width: '100%' }}
        renderFast
      />
    );
  }
  if (type === 'selection' && dataKey === 'trailer_status') {
    return (
      <TrailerStatus
        id="trailer_status"
        name="trailer_status"
        defaultValue={value}
        onChange={onChange}
        sx={{ width: '100%' }}
        renderFast
      />
    );
  }
  if (type === 'selection' && dataKey === 'trailer_status_reason') {
    return (
      <TrailerStatusReason
        id="trailer_status_reason"
        name="trailer_status_reason"
        defaultValue={value}
        onChange={onChange}
        sx={{ width: '100%' }}
        renderFast
      />
    );
  }
  if (
    type === 'selection' &&
    (dataKey === 'quality_video' ||
      dataKey === 'quality_poster_16x9' ||
      dataKey === 'quality_trailer')
  ) {
    return (
      <QualityCheck
        id="quality_check"
        name="quality_check"
        defaultValue={value}
        onChange={onChange}
        sx={{ width: '100%' }}
        renderFast
      />
    );
  }
  if (type === 'selection' && dataKey === 'color') {
    return (
      <VideoColor
        id="booleans"
        name="booleans"
        defaultValue={value}
        onChange={onChange}
        sx={{ width: '100%' }}
        renderFast
      />
    );
  }
  if (type === 'selection' && dataKey === 'video_language') {
    return (
      <LanguageCodes
        id="video_language"
        name="video_language"
        label=""
        defaultValue={value}
        onChange={onChange}
        sx={{ width: '100%' }}
        renderFast
        nullOption
      />
    );
  }
  if (type === 'timing' && dataKey === 'ad_timing') {
    const videoDuration = 240;
    const [inputValue, setInputValue] = useState('');
    const [adTimings, setAdTimings] = useState([]);
    const [isDialogOpen, setIsDialogOpen] = useState(false);

    useEffect(() => {
      if (typeof value === 'string' && value.length > 0) {
        setAdTimings(value.split(',').map((time) => time.trim()));
      } else {
        setAdTimings([]);
      }
    }, [value]);

    const handleAddTiming = () => {
      if (!inputValue) return;

      const updatedTimings = [...adTimings, inputValue]
        .map((time) => time.trim())
        .filter((time) => time)
        .sort((a, b) =>
          dayjs(a, 'HH:mm:ss').isBefore(dayjs(b, 'HH:mm:ss')) ? -1 : 1
        );

      setAdTimings(updatedTimings);
      setInputValue('');

      onChange({
        target: { value: updatedTimings.join(',') },
      });
    };

    const handleRemoveTiming = (index) => {
      const updatedTimings = adTimings.filter((_, i) => i !== index);
      setAdTimings(updatedTimings);
      onChange({
        target: { value: updatedTimings.join(',') },
      });
    };

    const handleSaveGeneratedTimings = (timings) => {
      setAdTimings(timings);
      onChange({
        target: { value: timings.join(',') },
      });
      setIsDialogOpen(false);
    };

    return (
      <>
        <Grid
          item
          xs={12}
          container
          alignItems="center"
          spacing={2}
          style={{
            width: '200px',
            maxWidth: '200px',
          }}
        >
          <Grid
            item
            xs={12}
            container
            justifyContent="center"
            alignContent="center"
          >
            <Button
              onClick={() => setIsDialogOpen(true)}
              color="secondary"
              variant="contained"
              maxWidth="sm"
            >
              Generate Ad Timings
            </Button>
          </Grid>
          <Grid item xs={12}>
            <TimeInput value={inputValue} onChange={(e) => setInputValue(e)} />
          </Grid>
          <Grid
            item
            xs={12}
            container
            alignItems="center"
            justifyContent="center"
          >
            <IconButton onClick={handleAddTiming}>
              <Iconify icon="lets-icons:add-duotone" width={30} />
            </IconButton>
          </Grid>
          <Grid
            item
            xs={12}
            container
            justifyContent="center"
            alignContent="center"
          >
            {adTimings.map((time, index) => (
              <Chip
                key={index}
                label={time}
                onDelete={() => handleRemoveTiming(index)}
                style={{ margin: '4px' }}
                variant="outlined"
              />
            ))}
          </Grid>
        </Grid>

        <GenerateAdTimingsModal
          open={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
          durationInMinutes={videoDuration}
          onSave={handleSaveGeneratedTimings}
        />
      </>
    );
  }

  return <FastTextInput value={value} onChange={onChange} width={width} />;
};

EditableCellValue.propTypes = {
  type: PropTypes.string.isRequired,
  dataKey: PropTypes.string.isRequired,
  value: PropTypes.any.isRequired,
  onChange: PropTypes.func.isRequired,
  width: PropTypes.string.isRequired,
};

export default EditableCellValue;
